import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

export function AuthModal({ open, onOpenChange, onSuccess }) {
  const { loginSuccess } = useAuth();
  const [tab, setTab] = useState("login"); // login | register | forgot | reset
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      if (tab === "register") {
        const { data } = await api.post("/auth/register", form);
        loginSuccess(data);
        toast.success("Hesabınız oluşturuldu, hoş geldiniz!");
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        loginSuccess(data);
        toast.success("Giriş başarılı!");
      }
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const sendForgot = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      toast.success(data.message);
      setTab("reset");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const doReset = async () => {
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email: form.email, code: resetCode, new_password: newPassword });
      toast.success("Şifreniz güncellendi. Giriş yapabilirsiniz.");
      setTab("login");
      setNewPassword(""); setResetCode("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const title = { login: "Giriş Yap", register: "Üye Ol", forgot: "Şifremi Unuttum", reset: "Yeni Şifre Belirle" }[tab];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold text-slate-900">{title}</DialogTitle>
        </DialogHeader>

        {(tab === "login" || tab === "register") && (
          <>
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
              <button data-testid="auth-tab-login" onClick={() => setTab("login")}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "login" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>Giriş Yap</button>
              <button data-testid="auth-tab-register" onClick={() => setTab("register")}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "register" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>Üye Ol</button>
            </div>

            <div className="space-y-3 mt-2">
              {tab === "register" && (
                <div><Label className="text-sm">Ad Soyad</Label>
                  <Input data-testid="auth-name-input" value={form.name} onChange={upd("name")} placeholder="Adınız Soyadınız" className="rounded-xl mt-1" /></div>
              )}
              <div><Label className="text-sm">E-posta</Label>
                <Input data-testid="auth-email-input" type="email" value={form.email} onChange={upd("email")} placeholder="ornek@eposta.com" className="rounded-xl mt-1" /></div>
              <div><Label className="text-sm">Şifre</Label>
                <Input data-testid="auth-password-input" type="password" value={form.password} onChange={upd("password")} placeholder="••••••••" className="rounded-xl mt-1" onKeyDown={(e) => e.key === "Enter" && submit()} /></div>
              {tab === "login" && (
                <button data-testid="auth-forgot-link" onClick={() => setTab("forgot")} className="text-sm text-emerald-600 font-semibold hover:underline">Şifremi Unuttum?</button>
              )}
              <Button data-testid="auth-submit-button" onClick={submit} disabled={loading} className="w-full brand-gradient text-white rounded-xl h-11 font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tab === "login" ? "Giriş Yap" : "Hesap Oluştur"}
              </Button>
            </div>

            <div className="flex items-center gap-3 my-1">
              <div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">veya</span><div className="h-px bg-slate-200 flex-1" />
            </div>
            <Button data-testid="auth-google-button" onClick={googleLogin} variant="outline" className="w-full rounded-xl h-11 font-semibold border-slate-300">
              <img alt="google" src="https://www.svgrepo.com/show/475656/google-color.svg" className="h-5 w-5 mr-2" /> Google ile devam et
            </Button>
            <p className="text-xs text-slate-400 flex items-center gap-1.5 justify-center mt-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Bilgileriniz güvende, gizli tutulur.
            </p>
          </>
        )}

        {tab === "forgot" && (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-500">Kayıtlı e-posta adresinizi girin, size 6 haneli bir sıfırlama kodu gönderelim.</p>
            <div><Label className="text-sm">E-posta</Label>
              <Input data-testid="forgot-email-input" type="email" value={form.email} onChange={upd("email")} placeholder="ornek@eposta.com" className="rounded-xl mt-1" /></div>
            <Button data-testid="forgot-submit-button" onClick={sendForgot} disabled={loading || !form.email} className="w-full brand-gradient text-white rounded-xl h-11 font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kodu E-postama Gönder"}
            </Button>
            <button onClick={() => setTab("login")} className="text-sm text-slate-500 flex items-center gap-1 hover:text-emerald-600"><ArrowLeft className="h-4 w-4" /> Girişe dön</button>
          </div>
        )}

        {tab === "reset" && (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-500">E-postanıza gelen 6 haneli kodu ve yeni şifrenizi girin.</p>
            <div><Label className="text-sm">Sıfırlama Kodu (6 haneli)</Label>
              <Input data-testid="reset-code-input" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="000000" className="rounded-xl mt-1 tracking-[0.5em] text-center font-mono text-lg" /></div>
            <div><Label className="text-sm">Yeni Şifre</Label>
              <Input data-testid="reset-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="rounded-xl mt-1" /></div>
            <Button data-testid="reset-submit-button" onClick={doReset} disabled={loading || resetCode.length !== 6 || !newPassword} className="w-full brand-gradient text-white rounded-xl h-11 font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Şifreyi Güncelle"}
            </Button>
            <button onClick={() => setTab("login")} className="text-sm text-slate-500 flex items-center gap-1 hover:text-emerald-600"><ArrowLeft className="h-4 w-4" /> Girişe dön</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
